import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

const col1 = categories.slice(0, 5);
const col2 = categories.slice(5, 9);
const col3 = categories.slice(9);

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setStatus('success');
      setEmail('');
      return;
    }

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      await fetch('https://readdy.ai/api/form/d8ugj7lu37m8lq7g35a0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setStatus('success');
      setEmail('');
    } catch {
      setStatus('error');
    }

    setTimeout(() => setStatus('idle'), 4000);
  };

  return (
    <footer className="bg-background-50 border-t border-background-200/60">
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          <div className="lg:w-[40%]">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              Join the
            </h2>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-primary-600">
              KakobuySpreadsheet
            </h2>
            <p className="text-sm text-foreground-500 mt-4 leading-relaxed max-w-sm">
              <strong>KakobuySpreadsheet</strong> is an independent buying resource that helps users discover trusted spreadsheet links, seller guides, and shopping information. Founded in 2026, our mission is to make spreadsheet-based shopping safer and more accessible.
            </p>

            <form onSubmit={handleSubmit} className="mt-8" data-readdy-form="" action="https://readdy.ai/api/form/d8ugj7lu37m8lq7g35a0" method="POST">
              <label className="text-xs text-foreground-500 font-medium mb-2 block">
                Subscribe for updates
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="flex-1 bg-transparent border-b border-background-300 px-1 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                />
                <button
                  type="submit"
                  className="whitespace-nowrap px-6 py-2 rounded-full border border-foreground-300 text-sm font-medium text-foreground-700 hover:bg-foreground-900 hover:text-background-50 hover:border-foreground-900 transition-all duration-200 cursor-pointer"
                >
                  Subscribe
                </button>
              </div>
              {status === 'success' && (
                <p className="text-xs text-secondary-700 mt-2">Thanks for subscribing!</p>
              )}
              {status === 'error' && (
                <p className="text-xs text-accent-600 mt-2">Something went wrong. Please try again.</p>
              )}
              <div style={{ position: 'absolute', left: '-9999px', opacity: 0 }} aria-hidden="true">
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" />
              </div>
            </form>
          </div>

          <div className="lg:w-[60%] grid grid-cols-2 sm:grid-cols-3 gap-8">
            <div>
              <h4 className="font-heading font-semibold text-foreground-900 text-sm mb-4">Shoes & Clothing</h4>
              <ul className="space-y-2.5">
                {col1.map((cat) => (
                  <li key={cat.id}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-semibold text-foreground-900 text-sm mb-4">Accessories & More</h4>
              <ul className="space-y-2.5">
                {col2.map((cat) => (
                  <li key={cat.id}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-semibold text-foreground-900 text-sm mb-4">Lifestyle & Other</h4>
              <ul className="space-y-2.5">
                {col3.map((cat) => (
                  <li key={cat.id}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
                <li>
                  <Link
                    to="/kakobuy-spreadsheet-beginners-guide"
                    className="text-sm text-primary-600 font-medium hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Beginner&apos;s Guide
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-background-200/60 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-500">
            &copy; {new Date().getFullYear()} <strong>KakobuySpreadsheet</strong>. All rights reserved. Last Updated: June 2026.
          </p>
          <div className="flex items-center gap-6">
            <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-xs text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              Main Site
            </a>
            <Link to="/kakobuy-spreadsheet-beginners-guide" className="text-xs text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              Beginner&apos;s Guide
            </Link>
            <a href="#hero" className="text-xs text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              Back to Top
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}